import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ConciergeLogo from "../../assets/images/conciergeLogoLight.svg";
import { api, googleEvent, hasConversionBeenSent, markConversionSent, getBookingConversionValue } from "../../api";
import { getEnvironmentConfig } from "../../utils/environment";
import { formatArrivalTime } from "../../utils/formatArrivalTime";
import "../../views/BookingFlow/BookingFlow.css";

const BookingSuccess = ({ bookingData, cartData, ReactGA, GTM, fbEvent }) => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [localBookingData, setLocalBookingData] = useState(bookingData || null);
  const [localCartData, setLocalCartData] = useState(cartData || null);
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const eventsFired = useRef(false); // Track if events have been fired

  // Get environment configuration
  const envConfig = getEnvironmentConfig();

  // Function to clear cart cookie
  const clearCartCookie = () => {
    document.cookie =
      "ccw_booking_cart_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    console.log("Cart cookie cleared");
  };

  // Fetch booking data if not provided via props or if bookingId is available
  useEffect(() => {
    if (bookingId) {
      setLoading(true);
      fetchBookingData();
    }

    // Clear cart cookie when component mounts (on success page)
    clearCartCookie();
  }, [bookingId]);

  const fetchBookingData = async () => {
    console.log("fetchBookingData called with bookingId:", bookingId);
    try {
      const response = await api(`booking-flow/booking/${bookingId}`, "GET");
      console.log("API response:", response);
      if (response.status === 200) {
        setLocalBookingData(response.data.booking);
        setLocationData(response.data.location);

        // Cart data is already included in the response
        if (response.data.cart) {
          setLocalCartData(response.data.cart);
          console.log("Set localCartData:", response.data.cart);
        }
      }
    } catch (error) {
      console.error("Error fetching booking data:", error);
      // Clear cookie if it's a 404 error (booking not found)
      if (error.response?.status === 404) {
        clearCartCookie();
      }
    } finally {
      setLoading(false);
    }
  };

  // Use local data if available, otherwise use props
  const finalBookingData = localBookingData || bookingData;
  const finalCartData = localCartData || cartData;

  console.log("Component render - bookingId:", bookingId);
  console.log("Component render - bookingData prop:", bookingData);
  console.log("Component render - cartData prop:", cartData);
  console.log("Component render - localBookingData:", localBookingData);
  console.log("Component render - localCartData:", localCartData);
  console.log("Component render - finalBookingData:", finalBookingData);
  console.log("Component render - finalCartData:", finalCartData);

  // Log when component mounts
  React.useEffect(() => {
    console.log("BookingSuccess component mounted with bookingId:", bookingId);
    console.log("Initial props - bookingData:", bookingData);
    console.log("Initial props - cartData:", cartData);
  }, []);

  // Log when data changes
  React.useEffect(() => {
    console.log("Data changed - finalCartData:", finalCartData);
    console.log(
      "Data changed - finalCartData?.step1Data:",
      finalCartData?.step1Data
    );
    console.log(
      "Data changed - finalCartData?.step1Data?.selectedLocation:",
      finalCartData?.step1Data?.selectedLocation
    );
  }, [finalCartData]);

  // Fire tracking events when booking data is available (only once)
  useEffect(() => {
    // Only fire events if we have booking data and events haven't been fired yet
    if (
      eventsFired.current ||
      !finalBookingData
    ) {
      return;
    }

    // Prefer iPOS id for analytics transaction_id; URL bookingId may be Mongo _id
    const iPosId =
      finalBookingData?.posBookingKey ||
      finalBookingData?.bookingKey ||
      finalBookingData?.posResponse?.BookingKey ||
      null;
    const bookingNumber = iPosId || bookingId;

    if (!bookingNumber) {
      return;
    }

    if (
      hasConversionBeenSent(bookingNumber) ||
      hasConversionBeenSent(bookingId) ||
      (iPosId && hasConversionBeenSent(iPosId))
    ) {
      eventsFired.current = true;
      return;
    }

    // Persist before firing so a remount/refresh cannot send Purchase again
    markConversionSent(bookingNumber);
    if (bookingId) markConversionSent(bookingId);
    if (iPosId) markConversionSent(iPosId);
    eventsFired.current = true;

    const estimatedTotal = getBookingConversionValue(
      finalBookingData,
      finalCartData,
    );

    // Fire Facebook Pixel Purchase event
    if (fbEvent && estimatedTotal > 0) {
      fbEvent("Purchase", {
        currency: "AUD",
        value: Number(estimatedTotal),
      });
    }

    // Fire Google Analytics/GTM booking_confirmed event (independent of Pixel)
    if (ReactGA && GTM) {
      googleEvent(ReactGA, GTM, "booking_confirmed", {
        bookingNumber,
        iPosId: iPosId || undefined,
      });
    } else if (ReactGA) {
      googleEvent(ReactGA, null, "booking_confirmed", {
        bookingNumber,
        iPosId: iPosId || undefined,
      });
    }

    console.log("Tracking events fired for booking:", bookingNumber, {
      iPosId,
    });
  }, [finalBookingData, finalCartData, ReactGA, GTM, fbEvent, bookingId]);

  // Define helper functions first
  const getLocationName = () => {
    const name =
      finalCartData?.step1Data?.selectedLocation?.name ||
      finalCartData?.selectedLocation?.name ||
      locationData?.name ||
      finalBookingData?.idLocation?.name ||
      finalBookingData?.location?.name ||
      "Selected Location";
    console.log("getLocationName() returning:", name);
    return name;
  };

  const getLocationAddress = () => {
    const address =
      finalCartData?.step1Data?.selectedLocation?.address ||
      finalCartData?.selectedLocation?.address ||
      locationData?.address ||
      finalBookingData?.idLocation?.address ||
      finalBookingData?.location?.address ||
      "Address not available";
    console.log("getLocationAddress() returning:", address);
    return address;
  };

  const handleTakeMeThere = () => {
    const address = getLocationAddress();
    if (address && address !== "Address not available") {
      const encodedAddress = encodeURIComponent(address);
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
        "_blank"
      );
    }
  };

  const handleManageBooking = () => {
    // Get the booking number from the booking data
    const bookingNumber =
      finalBookingData?.posBookingKey ||
      finalBookingData?.bookingKey ||
      bookingId;

    // Link to manage booking page with booking number as query parameter
    const manageBookingUrl = `${envConfig.bookingUrl}manage-booking?bookingid=${bookingNumber}`;
    window.open(manageBookingUrl, "_blank");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      // Handle different date formats
      if (dateString.includes(",")) {
        // Handle format like "Wednesday, October 1st 2025"
        const parts = dateString.split(",");
        if (parts.length >= 2) {
          const dayPart = parts[0].trim();
          const datePart = parts[1].trim();
          return `${dayPart}, ${datePart}`;
        }
      }

      const date = new Date(dateString);
      return date.toLocaleDateString("en-AU", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeData) => {
    if (!timeData) return "N/A";

    try {
      // Handle time object structure from booking
      if (
        typeof timeData === "object" &&
        timeData.hour &&
        timeData.minute &&
        timeData.ampm
      ) {
        return `${timeData.hour}:${timeData.minute} ${timeData.ampm}`;
      }

      // Handle time format like "09:00" or "9:00 AM"
      if (typeof timeData === "string" && timeData.includes(":")) {
        const [hours, minutes] = timeData.split(":");
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? "PM" : "AM";
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
      }
      return timeData;
    } catch (error) {
      return timeData;
    }
  };

  if (loading) {
    return (
      <div className="booking-success-container">
        <div className="booking-success-content">
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading booking details...</p>
              <p>Booking ID: {bookingId}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-success-container">
      <div className="booking-success-content">
        {/* Header */}
        <div className="booking-success-header">
          <div className="logo">
            <img
              src={ConciergeLogo}
              alt="Concierge Car Wash"
              className="logo-image"
            />
          </div>
          <h1 className="success-title">Booking Confirmed!</h1>
          <p className="success-subtitle">
            Your booking has been successfully confirmed. We look forward to
            seeing you!
          </p>
        </div>

        {/* Booking Details */}
        <div className="booking-details-section">
          <div className="booking-detail-item">
            <span className="detail-label">Booking Number</span>
            <span className="detail-value booking-number">
              {finalBookingData?.posBookingKey || "N/A"}
            </span>
          </div>

          <div className="booking-detail-item">
            <span className="detail-label">Expected Arrival Time</span>
            <span className="detail-value">
              {formatDate(
                finalCartData?.step1Data?.selectedDate ||
                  finalCartData?.selectedDate ||
                  finalBookingData?.date ||
                  finalBookingData?.bookingDate
              )}{" "}
              at{" "}
              {formatArrivalTime(
                finalCartData?.step1Data?.selectedTimeSlot
              ) ||
                formatTime(
                  finalCartData?.step1Data?.selectedTimeSlot?.timeKey ||
                    finalCartData?.selectedTime ||
                    finalBookingData?.time ||
                    finalBookingData?.bookingTime
                )}
            </span>
          </div>

          <div className="booking-detail-item">
            <span className="detail-label">Location</span>
            <span className="detail-value">{getLocationName()}</span>
          </div>

          <div className="booking-detail-item">
            <span className="detail-label">Address</span>
            <span className="detail-value address">{getLocationAddress()}</span>
          </div>

          {/* Service Details */}
          {finalCartData?.step2Data?.selectedBundle && (
            <div className="booking-detail-item">
              <span className="detail-label">Service</span>
              <span className="detail-value">
                {finalCartData.step2Data.selectedBundle.name || "Express Wash"}
              </span>
            </div>
          )}

          {/* Extras */}
          {finalBookingData?.posResponse?.BookingNotes &&
            finalBookingData.posResponse.BookingNotes.includes("Extras:") && (
              <div className="booking-detail-item">
                <span className="detail-label">Extras</span>
                <span className="detail-value">
                  {(() => {
                    const notes = finalBookingData.posResponse.BookingNotes;
                    const extrasMatch = notes.match(/Extras: (.+?)(?:\n|$)/);
                    return extrasMatch ? extrasMatch[1] : "None";
                  })()}
                </span>
              </div>
            )}

          {/* Estimated Time */}
          {finalCartData?.step2Data?.selectedBundle?.duration && (
            <div className="booking-detail-item">
              <span className="detail-label">Estimated Time</span>
              <span className="detail-value">
                {finalCartData.step2Data.selectedBundle.duration} minutes
              </span>
            </div>
          )}

          {/* Estimated Cost */}
          {finalCartData?.step2Data?.selectedBundle?.price && (
            <div className="booking-detail-item">
              <span className="detail-label">Estimated Cost</span>
              <span className="detail-value">
                ${finalCartData.step2Data.selectedBundle.price}
                {finalCartData?.step3Data?.bookingFee && (
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      display: "block",
                    }}
                  >
                    + ${finalCartData.step3Data.bookingFee} booking fee
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Location Actions */}
        <div className="location-actions">
          <button className="take-me-there-button" onClick={handleTakeMeThere}>
            🚗 Take me there
          </button>
        </div>

        {/* What's Next Section */}
        <div className="whats-next-section">
          <h3 className="whats-next-title">What's next?</h3>
          <p className="whats-next-text">
            Please arrive at your scheduled time. Use the button below to manage
            your booking, make changes, or contact support if needed.
          </p>
          <button
            className="manage-booking-button"
            onClick={handleManageBooking}
          >
            Manage Booking
          </button>
          <div className="how-to-section">
            <h4 className="how-to-title">How to find us:</h4>
            <p className="how-to-text">
              When you arrive at the location, look for our Concierge Car Wash
              signage and parking bays. Our team will be ready to assist you at
              your scheduled time.
            </p>
          </div>
          <p className="contact-info">
            <strong>
              Need help? Contact:{" "}
              <a href="tel:1300279002" className="phone-link">
                1300 279 002
              </a>
            </strong>
          </p>
        </div>

        {/* Additional Actions */}
        <div className="additional-actions">
          <button
            className="secondary-button"
            onClick={() => (window.location.href = envConfig.promotionalUrl)}
          >
            Back to Home
          </button>
          <button
            className="primary-button"
            onClick={() => (window.location.href = envConfig.bookingUrl)}
          >
            Book Another
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;
